import React, { useEffect, useState } from "react";
import { Download, FileDown } from "lucide-react";

const AUDIT_SIZES = [10, 20, 50];
import { useApi, useDebounced } from "../hooks/useApi";
import { downloadPdf } from "./FileUpload";
import { Button } from "./ui/button";
import { ServerPager, SearchBox } from "./Pager";
import { qs } from "./AttendanceReport";
import { OptionSelect, DateRange } from "./AttendanceTable";

const EMPTY = { q: "", entity: "", action: "", actor: "", date_from: "", date_to: "" };

function AuditFilters({ f, set, facets, exportQs }) {
  const active = Object.keys(EMPTY).some((k) => f[k]);
  return (
    <div className="mb-3 flex flex-wrap items-end gap-2">
      <SearchBox value={f.q} onChange={(v) => set({ q: v })} placeholder="Search actor, action, entity…" testId="audit-search" className="w-60" />
      <OptionSelect value={f.entity} onChange={(v) => set({ entity: v })} label="All categories" testId="audit-entity" className="w-40" items={facets.entities.map((e) => [e, e])} />
      <OptionSelect value={f.action} onChange={(v) => set({ action: v })} label="All actions" testId="audit-action" className="w-48" items={facets.actions.map((a) => [a, a])} />
      <OptionSelect value={f.actor} onChange={(v) => set({ actor: v })} label="All users" testId="audit-actor" className="w-44" items={facets.actors.map((a) => [a.id, a.name])} />
      <DateRange from={f.date_from} to={f.date_to} onFrom={(v) => set({ date_from: v })} onTo={(v) => set({ date_to: v })} testPrefix="audit" />
      {active && <Button size="sm" variant="ghost" onClick={() => set(EMPTY)} data-testid="audit-clear">Clear</Button>}
      <div className="ml-auto flex gap-2">
        <Button size="sm" variant="outline" className="gap-1" data-testid="audit-export-csv" onClick={() => downloadPdf(`/admin/audit-log.csv?${exportQs}`, "audit-log.csv")}><Download size={14} /> CSV</Button>
        <Button size="sm" className="gap-1 bg-crimson hover:bg-crimson-hover" data-testid="audit-export-pdf" onClick={() => downloadPdf(`/admin/audit-log.pdf?${exportQs}`, "audit-log.pdf")}><FileDown size={14} /> PDF</Button>
      </div>
    </div>
  );
}

const details = (d) => Object.entries(d || {}).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ");

function AuditRow({ a }) {
  return (
    <tr className="border-b border-gold/15" data-testid={`audit-${a.id}`}>
      <td className="p-3 font-mono-accent text-xs">{a.timestamp?.slice(0, 19).replace("T", " ")}</td>
      <td className="p-3">{a.actor_name} <span className="text-xs text-muted-foreground">({a.actor_role})</span></td>
      <td className="p-3 font-mono-accent text-xs">{a.action}</td>
      <td className="p-3">{a.entity}</td>
      <td className="p-3 max-w-xs truncate text-xs text-muted-foreground" title={JSON.stringify(a.details || {})}>{details(a.details)}</td>
    </tr>
  );
}

export function AuditLogTable() {
  const [f, setF] = useState({ ...EMPTY, page: 1, page_size: Number(localStorage.getItem("audit-size")) || 20 });
  const set = (patch) => setF((s) => ({ ...s, ...patch, page: patch.page ?? 1 }));
  const dq = useDebounced(f.q);
  const { data } = useApi(`/admin/audit-log?${qs({ ...f, q: dq })}`);
  const [facets, setFacets] = useState({ entities: [], actions: [], actors: [] });
  useEffect(() => { if (data?.entities) setFacets({ entities: data.entities, actions: data.actions, actors: data.actors }); }, [data]);
  return (
    <div>
      <AuditFilters f={f} set={set} facets={facets} exportQs={qs({ ...f, page: undefined, page_size: undefined })} />
      <div className="glass overflow-x-auto rounded-2xl">
        <table className="w-full text-sm" data-testid="audit-table"><thead className="bg-crimson text-parchment"><tr>
          <th className="p-3 text-left">Time</th><th className="p-3 text-left">Actor</th>
          <th className="p-3 text-left">Action</th><th className="p-3 text-left">Category</th><th className="p-3 text-left">Details</th></tr></thead>
          <tbody>
            {(data?.items || []).map((a) => <AuditRow key={a.id} a={a} />)}
            {data && data.items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No audit entries.</td></tr>}
          </tbody>
        </table>
        {data && <ServerPager page={data.page} pages={data.pages} total={data.total} pageSize={data.page_size} sizes={AUDIT_SIZES} noun="entries"
          onPage={(p) => set({ page: p })} onSize={(n) => { localStorage.setItem("audit-size", String(n)); set({ page_size: n }); }} testId="audit-pager" />}
      </div>
    </div>
  );
}
